
import axios, {AxiosError, AxiosResponse} from 'axios';
import {
  defineComponent, onMounted, ref, nextTick, inject, onUpdated, reactive
} from 'vue';
import {decode} from 'html-entities';
import VueScrollTo from 'vue-scrollto';

type Alert = {
  message: string;
  type: string;
};

export default defineComponent({
  props: {
    action: {
      type: String,
      required: true,
    },
    onSuccess: {
      type: Function,
      default: null,
    },
    resetFormOnSuccess: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const action = ref(props.action);
    const form = ref<HTMLFormElement | null>(null);
    const scrollOffset = inject('scrollOffset') as number;
    const lazyLoad: any = inject('lazyLoad');

    const htmlResponse = ref('');
    const isLoading = ref<boolean>(false);
    let properties: any = {} as { [key: string]: any };
    let alert = ref<Alert | null>(null);

    onMounted(() => {
      form.value?.addEventListener('submit', (e) => {
        e.preventDefault();
        send();
      });
      lazyLoad.update();
    });

    const getErrorMessage = (propertiesKeys: string) => {
      let messages: Array<string> = [];
      for (let property of propertiesKeys.split(',')) {
        property = property.trim();
        if (properties[property]) {
          messages = messages.concat(properties[property].map((error: { message: string }) => error.message));
        }
      }
      return messages.join('<br>');
    };

    const handleErrorResponse = async (error: AxiosError) => {
      if (error.response) {
        properties = error.response.data;
      } else {
        console.error('Oops an error occurred.');
      }
    };

    onUpdated(() => {
      const firstErrorElement = form.value?.querySelector(".is-invalid");
      if (firstErrorElement) {
        VueScrollTo.scrollTo(firstErrorElement, {
          offset: -100,
        });
      }
    })

    const handleSuccessResponse = (response: AxiosResponse) => {
      if (response.data && response.data.content) {
        htmlResponse.value = decode(response.data.content);
        nextTick(() => {
          const firstSuccessElement = form.value?.querySelector('.form-success');
          if (firstSuccessElement) {
            VueScrollTo.scrollTo(firstSuccessElement, {
              offset: -100,
            });
          }
        });
      }
      if (props.onSuccess) {
        props.onSuccess();
      }
      reset(props.resetFormOnSuccess);
    };

    const hasPropertyError = (propertiesKeys: string) => {
      for (let property of propertiesKeys.split(',')) {
        property = property.trim();
        if (properties[property] && properties[property].length) {
          return true;
        }
      }
      return false;
    };

    const reset = (resetForm: boolean) => {
      properties = [];
      if (resetForm) {
        form.value?.reset();
      }
    };

    const send = () => {
      if (form.value) {
        const formData = new FormData(form.value);
        let responseData:any = null;

        isLoading.value = true;
        alert.value = null;

        axios.post(action.value, formData)
          .then((response) => {
            handleSuccessResponse(response);
            responseData = response.data;
          })
          .catch((error) => {
            handleErrorResponse(error);
            responseData = error.response?.data;
          })
          .then(() => {
            if (responseData) {
              if (responseData.redirect) {
                window.location.href = responseData.redirect;

                return;
              }

              if (responseData.alert) {
                alert.value = {
                  message: responseData.alert.message,
                  type: responseData.alert.type,
                };
              }
            }

            isLoading.value = false;
          });
      }
    };

    return {
      action,
      alert,
      form,
      htmlResponse,
      isLoading,
      send,
      hasPropertyError,
      getErrorMessage,
    };
  },
});
